import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Box,
    Button,
    ButtonGroup,
    MenuItem,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { LessonPlan } from '../../object-actions/types/types';
import LessonPlanSlide from './LessonPlanSlide';

interface LessonPlanSlideDeckProps {
    plan: LessonPlan;
}

const LessonPlanSlideDeck: React.FC<LessonPlanSlideDeckProps> = ({ plan }) => {
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);

    const slides = plan.slide_deck || [];
    const totalSlides = slides.length;

    if (totalSlides === 0) {
        return (
            <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    No slides available in this lesson plan.
                </Typography>
            </Box>
        );
    }

    const handleSlideChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setActiveStep(Number(event.target.value));
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, totalSlides - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    const currentSlide = slides[activeStep];

    return (
        <Box sx={{ mt: 1 }}>
            {/* Slide Navigation */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 'bold',
                            color: 'text.primary'
                        }}
                    >
                        Slide Navigation
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {activeStep + 1} of {totalSlides}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        select
                        label="Select Slide"
                        variant="standard"
                        size="small"
                        value={activeStep}
                        onChange={handleSlideChange}
                        sx={{
                            flexGrow: 1
                        }}
                    >
                        {slides.map((slide, index) => (
                            <MenuItem key={index} value={index}>
                                {slide.title || `Slide ${index + 1}`}
                            </MenuItem>
                        ))}
                    </TextField>
                    <ButtonGroup variant="outlined" aria-label="slide navigation">
                        <Button
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<ArrowBackIcon />}
                        >
                            Previous
                        </Button>
                        <Button
                            onClick={handleNext}
                            disabled={activeStep === totalSlides - 1}
                            endIcon={<ArrowForwardIcon />}
                        >
                            Next
                        </Button>
                    </ButtonGroup>
                </Box>
            </Box>

            {/* Current Slide */}
            {currentSlide && (
                <LessonPlanSlide
                    slide={currentSlide}
                    slideNumber={activeStep + 1}
                    totalSlides={totalSlides}
                />
            )}
        </Box>
    );
};

export default LessonPlanSlideDeck;

